import ModalSurface from '../shared/ModalSurface';
import TestChip from '../breaker/TestChip';
import { useModuleMode } from '../../hooks/useModuleModes';
import { displayNumber, showTestRecords } from '../../lib/breakerPanel';
import ResponsiveRow from '../shared/ResponsiveRow';
import ResponsiveTable, { ResponsiveCell } from '../shared/ResponsiveTable';
import '../financials/financialsResponsive.css';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabaseClient } from '../../config/supabase';
import { getInvoiceDisplayAmount, getInvoiceDiscountLines, buildInvoiceAssemblySummary } from '../../lib/invoiceHelpers';
import { groupInvoicesForParentRollup, buildParentRollupStatementPayload, parseRollupNotes } from '../../lib/subAccountRollup';
import {
    DISPUTE_RESOLUTION_TYPES,
    DISPUTE_STATES,
    buildCreditNotePayload,
    buildDisputeAuditEntry,
    getDisputeReviewState,
    hasActiveDispute,
    resolveDispute,
    startDisputeReview,
} from '../../lib/disputeWorkflow';
import { isLineItemOverridden } from '../../lib/invoiceOverrideAudit';
import ChargeInvoiceAction from './ChargeInvoiceAction';
import useModuleAccess from '../../hooks/useModuleAccess';

export { getInvoiceDisplayAmount, getInvoiceDiscountLines, buildInvoiceAssemblySummary };

// ─── Lifecycle state machine ──────────────────────────────────────────────────

// All valid lifecycle_status values mirroring the DB check constraint.
export const LIFECYCLE_STATES = {
    // Quote sub-lifecycle
    DRAFT:            'draft',
    SENT:             'sent',
    PENDING_APPROVAL: 'pending_approval',
    APPROVED:         'approved',
    DECLINED:         'declined',
    // Invoice/payment spine
    ISSUED:           'issued',
    PARTIALLY_PAID:   'partially_paid',
    PAID:             'paid',
    CLOSED:           'closed',
    // Side exits (terminal)
    VOID:             'void',
    CANCELED:         'canceled',
    REFUNDED:         'refunded',
    WRITTEN_OFF:      'written_off',
};

// States that indicate the document is settled or exited — overdue badge suppressed.
const TERMINAL_OR_SETTLED_STATES = new Set([
    'paid', 'closed', 'void', 'canceled', 'refunded', 'written_off',
]);

// Human-readable labels for lifecycle states.
const LIFECYCLE_LABELS = {
    draft:            'Draft',
    sent:             'Sent',
    pending_approval: 'Pending Approval',
    approved:         'Approved',
    declined:         'Declined',
    issued:           'Issued',
    partially_paid:   'Partially Paid',
    paid:             'Paid',
    closed:           'Closed',
    void:             'Void',
    canceled:         'Canceled',
    refunded:         'Refunded',
    written_off:      'Written Off',
};

/**
 * Returns a styled <span> badge for a lifecycle_status value.
 * Falls back gracefully when lifecycle_status is absent (legacy rows).
 */
export function getLifecycleBadge(lifecycleStatus) {
    const state = lifecycleStatus?.toLowerCase();
    const label = LIFECYCLE_LABELS[state] || state || 'Unknown';

    if (state === 'paid' || state === 'closed') {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">{label}</span>;
    }
    if (state === 'issued' || state === 'partially_paid') {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">{label}</span>;
    }
    if (state === 'draft' || state === 'sent' || state === 'pending_approval') {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">{label}</span>;
    }
    if (state === 'approved') {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">{label}</span>;
    }
    if (state === 'declined') {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">{label}</span>;
    }
    if (state === 'void' || state === 'canceled') {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-200 text-gray-700">{label}</span>;
    }
    if (state === 'refunded') {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">{label}</span>;
    }
    if (state === 'written_off') {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">{label}</span>;
    }
    // Unknown / null — fall back to a neutral badge
    return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{label}</span>;
}

/**
 * Computes the four derived dashboard badges for a single invoice row.
 * These are computed from current state + related data; nothing is stored.
 *
 * @param {object}  invoice          - Invoice row from the database.
 * @param {Array}   recurringBilling - Rows from the recurring_billing table,
 *                                     which are scoped by account_id and carry a
 *                                     status ('active' / 'paused' / 'cancelled');
 *                                     matched to this invoice by account_id.
 * @returns {React.ReactElement[]}   - Array of badge elements (may be empty).
 */
export function computeDerivedBadges(invoice, recurringBilling = []) {
    const badges = [];
    const now = new Date();

    // Deposit-Held — set when a deposit payment has been collected and is being held.
    if (invoice?.deposit_held === true) {
        badges.push(
            <span key="deposit-held" className="px-2 py-1 text-xs font-semibold rounded-full bg-teal-100 text-teal-800">
                Deposit Held
            </span>
        );
    }

    // Pre-Authorized — set when a card has been pre-authorized but not yet captured.
    if (invoice?.pre_authorized === true) {
        badges.push(
            <span key="pre-auth" className="px-2 py-1 text-xs font-semibold rounded-full bg-cyan-100 text-cyan-800">
                Pre-Authorized
            </span>
        );
    }

    // Overdue — due_date is in the past and the invoice is not settled/exited.
    const state = invoice?.lifecycle_status?.toLowerCase();
    if (invoice?.due_date && !TERMINAL_OR_SETTLED_STATES.has(state)) {
        const dueDate = new Date(invoice.due_date);
        if (!isNaN(dueDate) && dueDate < now) {
            badges.push(
                <span key="overdue" className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                    Overdue
                </span>
            );
        }
    }

    // Auto-Billing-Active — the recurring_billing table (Gate 3) has a row for
    // this invoice's account_id with status = 'active'.
    const isAutoBilling = recurringBilling.some(
        (rb) => rb.status === 'active' && rb.account_id && rb.account_id === invoice?.account_id
    );
    if (isAutoBilling) {
        badges.push(
            <span key="auto-billing" className="px-2 py-1 text-xs font-semibold rounded-full bg-violet-100 text-violet-800">
                Auto-Billing
            </span>
        );
    }

    return badges;
}

/**
 * Returns an amber "Override" badge for a hand-set price line item.
 * Export kept alongside the other badge helpers for consistency.
 */
export function getOverrideBadge() {
    return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
            Override
        </span>
    );
}

export function getDisputeBadge(disputeState, resolutionType = null, hasCreditNotes = false) {
    if (disputeState === DISPUTE_STATES.DISPUTED) {
        return (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                Disputed
            </span>
        );
    }

    if (disputeState === DISPUTE_STATES.UNDER_REVIEW) {
        return (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                Under Review
            </span>
        );
    }

    if (resolutionType === DISPUTE_RESOLUTION_TYPES.REFUND) {
        return (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                Credited / Refunded
            </span>
        );
    }

    if (resolutionType === DISPUTE_RESOLUTION_TYPES.ADJUST) {
        return (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800">
                Adjusted
            </span>
        );
    }

    if (resolutionType === DISPUTE_RESOLUTION_TYPES.UPHOLD) {
        return (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-slate-200 text-slate-700">
                Upheld
            </span>
        );
    }

    if (hasCreditNotes) {
        return (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                Credit Note
            </span>
        );
    }

    return null;
}

/**
 * Drill-down panel showing the immutable audit trail for overridden line items
 * on a given invoice.  Fetches from invoice_line_item_overrides on demand.
 *
 * @param {object} props
 * @param {string} props.invoiceId  Invoice whose overrides to display.
 * @param {string} [props.theme]    'dark' | 'light'
 */
export function OverrideAuditPanel({ invoiceId, theme }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!invoiceId) { setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        supabaseClient
            .from('invoice_line_item_overrides')
            .select('id, line_item_id, overridden_by, overridden_at, old_value, new_value, override_reason')
            .eq('invoice_id', invoiceId)
            .order('overridden_at', { ascending: false })
            .then(({ data, error: fetchError }) => {
                if (cancelled) return;
                if (fetchError) { setError(fetchError.message); }
                else { setEntries(data || []); }
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [invoiceId]);

    const formatCurrency = (v) =>
        v != null
            ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v)
            : '—';
    const formatTs = (ts) =>
        ts ? new Date(ts).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

    if (loading) {
        return <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Loading override history…</p>;
    }
    if (error) {
        return <p className="text-xs text-red-600">Could not load override history: {error}</p>;
    }
    if (entries.length === 0) {
        return <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>No override audit entries.</p>;
    }

    return (
        <div className="space-y-2">
            {entries.map((entry) => (
                <div
                    key={entry.id}
                    className={`rounded-md border px-3 py-2 text-xs ${
                        theme === 'dark'
                            ? 'border-amber-700 bg-amber-900/30 text-amber-200'
                            : 'border-amber-200 bg-amber-50 text-amber-900'
                    }`}
                >
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{entry.overridden_by}</span>
                        <span className={theme === 'dark' ? 'text-amber-400' : 'text-amber-600'}>
                            {formatTs(entry.overridden_at)}
                        </span>
                    </div>
                    <div className="mt-1">
                        {formatCurrency(entry.old_value)} → {formatCurrency(entry.new_value)}
                    </div>
                    {entry.override_reason && (
                        <div className={`mt-1 italic ${theme === 'dark' ? 'text-amber-300' : 'text-amber-700'}`}>
                            "{entry.override_reason}"
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

export function DisputeAuditPanel({ invoiceId, theme, pendingEntries = [] }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!invoiceId) { setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        const disputeAuditQuery = supabaseClient?.from?.('invoice_dispute_audit_log');
        if (!disputeAuditQuery?.select) {
            setLoading(false);
            return;
        }
        disputeAuditQuery
            .select('id, line_item_id, action_type, actor, reason, from_state, to_state, resolution_type, occurred_at, metadata')
            .eq('invoice_id', invoiceId)
            .order('occurred_at', { ascending: false })
            .then(({ data, error: fetchError }) => {
                if (cancelled) return;
                if (fetchError) { setError(fetchError.message); }
                else { setEntries(data || []); }
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [invoiceId]);

    const mergedEntries = [...pendingEntries, ...entries];

    if (loading && mergedEntries.length === 0) {
        return <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Loading dispute history…</p>;
    }
    if (error && mergedEntries.length === 0) {
        return <p className="text-xs text-red-600">Could not load dispute history: {error}</p>;
    }
    if (mergedEntries.length === 0) {
        return <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>No dispute audit entries.</p>;
    }

    return (
        <div className="space-y-2">
            {mergedEntries.map((entry) => (
                <div
                    key={entry.id || `${entry.action_type}-${entry.occurred_at}`}
                    className={`rounded-md border px-3 py-2 text-xs ${
                        theme === 'dark'
                            ? 'border-rose-700 bg-rose-900/20 text-rose-100'
                            : 'border-rose-200 bg-rose-50 text-rose-900'
                    }`}
                >
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{entry.actor}</span>
                        <span className={theme === 'dark' ? 'text-rose-300' : 'text-rose-600'}>
                            {entry.occurred_at ? new Date(entry.occurred_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : 'Pending'}
                        </span>
                        <span className="uppercase tracking-wide">{entry.action_type}</span>
                        {entry.line_item_id && <span>Line item: {entry.line_item_id}</span>}
                    </div>
                    <div className="mt-1">
                        {entry.from_state || '—'} → {entry.to_state || 'resolved'}
                    </div>
                    {entry.reason && (
                        <div className={`mt-1 italic ${theme === 'dark' ? 'text-rose-200' : 'text-rose-700'}`}>
                            "{entry.reason}"
                        </div>
                    )}
                    {entry.resolution_type && (
                        <div className={`mt-1 ${theme === 'dark' ? 'text-rose-300' : 'text-rose-700'}`}>
                            Resolution: {entry.resolution_type}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function requireInvoiceOrgId(invoice) {
    if (!invoice?.org_id) {
        throw new Error('invoice.org_id is required for dispute actions');
    }

    return invoice.org_id;
}

/*
 * BL-173 Gate 5 — the refund leg of a dispute resolution runs on the server
 * (stripe-setup-intent → resolve_dispute_refund): it is the only place the Stripe refund
 * call, the Gate 8 threshold authority and the credit-note/audit writes belong. The client
 * sends an invoice id and a reason; it never sends an amount.
 */
export async function requestDisputeRefund({ invoiceId, reason, reasonCode = null, approvalId = null }) {
    if (!invoiceId) {
        throw new Error('An invoice is required to process a refund.');
    }

    const invokeFn = supabaseClient?.functions?.invoke;
    if (typeof invokeFn !== 'function') {
        throw new Error('The payments service is not configured in this environment.');
    }

    const { data, error: invokeError } = await supabaseClient.functions.invoke('stripe-setup-intent', {
        body: {
            action: 'resolve_dispute_refund',
            invoiceId,
            reason,
            ...(reasonCode ? { reasonCode } : {}),
            ...(approvalId ? { approvalId } : {}),
        },
    });

    if (invokeError) {
        throw new Error(invokeError.message || 'The refund could not be processed.');
    }
    if (data?.error) {
        throw new Error(data.error);
    }

    return data;
}

function DisputeResolutionPanel({ invoice, theme, onStartReview, onResolve, pendingAuditEntries = [], pendingCreditNotes = [] }) {
    const [actor, setActor] = useState('');
    const [resolutionReason, setResolutionReason] = useState('');
    const [resolutionAmount, setResolutionAmount] = useState(String(getInvoiceDisplayAmount(invoice) || ''));
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const disputeState = getDisputeReviewState(invoice);
    const disputedLineItems = Array.isArray(invoice?.line_items)
        ? invoice.line_items.filter((lineItem) => hasActiveDispute(lineItem))
        : [];
    const existingCreditNotes = Array.isArray(invoice?.credit_notes) ? invoice.credit_notes : [];
    const creditNotes = [...existingCreditNotes, ...pendingCreditNotes];

    useEffect(() => {
        setResolutionAmount(String(getInvoiceDisplayAmount(invoice) || ''));
    }, [invoice]);

    const handleStartReview = () => {
        try {
            const orgId = requireInvoiceOrgId(invoice);
            const updatedInvoice = {
                ...invoice,
                ...startDisputeReview({
                    dispute_state: disputeState,
                    filed_reason: invoice?.filed_reason,
                    filed_at: invoice?.filed_at,
                    resolved_by: invoice?.resolved_by,
                    resolved_at: invoice?.resolved_at,
                    resolution_type: invoice?.resolution_type,
                }),
            };

            const auditEntry = buildDisputeAuditEntry({
                orgId,
                invoiceId: invoice?.id,
                actor: actor || 'Staff reviewer',
                actionType: 'under_review',
                reason: resolutionReason || invoice?.filed_reason || 'Marked under review',
                fromState: disputeState,
                toState: DISPUTE_STATES.UNDER_REVIEW,
            });

            setError(null);
            onStartReview({ updatedInvoice, auditEntry });
        } catch (reviewError) {
            setError(reviewError.message);
        }
    };

    const handleResolve = async (resolutionType) => {
        try {
            const orgId = requireInvoiceOrgId(invoice);
            const resolvedBy = actor || 'Staff reviewer';
            const auditReason = resolutionReason || invoice?.filed_reason || 'Dispute resolved';
            const parsedResolutionAmount =
                typeof resolutionAmount === 'string' && resolutionAmount.trim() === ''
                    ? null
                    : Number(resolutionAmount);
            const { updatedTarget, resolutionAmount: normalizedAmount } = resolveDispute(invoice, {
                resolutionType,
                resolvedBy,
                resolutionAmount: parsedResolutionAmount,
            });

            const actionType = resolutionType === DISPUTE_RESOLUTION_TYPES.UPHOLD
                ? 'upheld'
                : resolutionType === DISPUTE_RESOLUTION_TYPES.ADJUST
                    ? 'adjusted'
                    : 'refunded';

            const auditEntry = buildDisputeAuditEntry({
                orgId,
                invoiceId: invoice?.id,
                actor: resolvedBy,
                actionType,
                reason: auditReason,
                fromState: disputeState,
                toState: null,
                resolutionType,
                metadata: normalizedAmount == null ? null : { resolution_amount: normalizedAmount },
            });

            // BL-173 Gate 5 — only the refund path touches Stripe, and it does so through
            // the server: the amount is resolved from the captured payment, the Gate 8
            // threshold authority is applied there, and the credit note + audit rows are
            // written server-side. Uphold and adjust stay pure state writes.
            let creditNote = null;
            if (resolutionType === DISPUTE_RESOLUTION_TYPES.REFUND) {
                setSubmitting(true);
                try {
                    const refundResult = await requestDisputeRefund({
                        invoiceId: invoice?.id,
                        reason: auditReason,
                    });

                    if (refundResult?.requiresDualControl) {
                        setError(refundResult.message
                            || 'This refund is above the dual-control threshold and is awaiting a second approver.');
                        return;
                    }

                    creditNote = refundResult?.creditNote ?? buildCreditNotePayload({
                        orgId,
                        invoice,
                        createdBy: resolvedBy,
                        reason: auditReason,
                        refundAmount: refundResult?.amount ?? normalizedAmount,
                    });
                } catch (refundError) {
                    setError(refundError.message || 'The refund could not be processed.');
                    return;
                } finally {
                    setSubmitting(false);
                }
            }

            setError(null);
            onResolve({
                updatedInvoice: {
                    ...invoice,
                    ...updatedTarget,
                    credit_notes: creditNote ? [...existingCreditNotes, creditNote] : existingCreditNotes,
                },
                auditEntry,
                creditNote,
            });
        } catch (resolutionError) {
            setError(resolutionError.message);
        }
    };

    return (
        <div className={`rounded-lg border p-4 space-y-4 ${theme === 'dark' ? 'border-rose-700 bg-gray-800' : 'border-rose-200 bg-rose-50'}`}>
            <div>
                <p className={`text-sm font-semibold ${'text-[var(--ink)]'}`}>Challenged-charge review</p>
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    State: <span className="font-medium">{disputeState || 'resolved'}</span>
                </p>
                {invoice?.filed_reason && (
                    <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Filed reason: {invoice.filed_reason}
                    </p>
                )}
                {invoice?.filed_at && (
                    <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Filed at: {new Date(invoice.filed_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                )}
            </div>

            {disputedLineItems.length > 0 && (
                <div>
                    <p className={`text-xs font-semibold mb-2 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>Disputed line items</p>
                    <div className="space-y-2">
                        {disputedLineItems.map((lineItem) => (
                            <div
                                key={lineItem.id || lineItem.description}
                                className={`rounded-md border px-3 py-2 text-xs ${
                                    theme === 'dark' ? 'border-orange-700 bg-orange-900/20 text-orange-100' : 'border-orange-200 bg-white text-orange-900'
                                }`}
                            >
                                <div className="font-semibold">{lineItem.description || lineItem.service_type_key || 'Line item'}</div>
                                <div className="mt-1">{lineItem.filed_reason || invoice?.filed_reason || 'Reason not captured yet'}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                    type="text"
                    value={actor}
                    onChange={(event) => setActor(event.target.value)}
                    placeholder="Resolved by"
                    className={`px-3 py-2 rounded-lg border text-sm ${
                        theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={resolutionAmount}
                    onChange={(event) => setResolutionAmount(event.target.value)}
                    placeholder="Adjustment / refund amount"
                    className={`px-3 py-2 rounded-lg border text-sm ${
                        theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
                <input
                    type="text"
                    value={resolutionReason}
                    onChange={(event) => setResolutionReason(event.target.value)}
                    placeholder="Resolution reason"
                    className={`px-3 py-2 rounded-lg border text-sm ${
                        theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
            </div>

            {error && <p className="text-xs text-red-600">{error}</p>}

            <div className="flex flex-wrap gap-2">
                {disputeState === DISPUTE_STATES.DISPUTED && (
                    <button
                        type="button"
                        onClick={handleStartReview}
                        className="px-3 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700"
                    >
                        Mark under review
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => handleResolve(DISPUTE_RESOLUTION_TYPES.UPHOLD)}
                    className="px-3 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium hover:bg-slate-800"
                >
                    Uphold
                </button>
                <button
                    type="button"
                    onClick={() => handleResolve(DISPUTE_RESOLUTION_TYPES.ADJUST)}
                    className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
                >
                    Adjust
                </button>
                <button
                    type="button"
                    onClick={() => handleResolve(DISPUTE_RESOLUTION_TYPES.REFUND)}
                    disabled={submitting}
                    className="px-3 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-60"
                >
                    {submitting ? 'Refunding…' : 'Refund'}
                </button>
            </div>

            {creditNotes.length > 0 && (
                <div>
                    <p className={`text-xs font-semibold mb-2 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>Credit notes</p>
                    <div className="space-y-2">
                        {creditNotes.map((creditNote, index) => (
                            <div
                                key={creditNote.id || creditNote.credit_note_number || `credit-${index}`}
                                className={`rounded-md border px-3 py-2 text-xs ${
                                    theme === 'dark' ? 'border-purple-700 bg-purple-900/20 text-purple-100' : 'border-purple-200 bg-white text-purple-900'
                                }`}
                            >
                                <div className="font-semibold">
                                    {creditNote.credit_note_number || 'Pending HUB-YYYYMMDD-NNN-CR assignment'}
                                </div>
                                <div className="mt-1">
                                    Amount: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(creditNote.refund_amount || 0)}
                                </div>
                                <div className="mt-1">{creditNote.reason}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div>
                <p className={`text-xs font-semibold mb-2 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>Dispute audit trail</p>
                <DisputeAuditPanel invoiceId={invoice?.id} theme={theme} pendingEntries={pendingAuditEntries} />
            </div>
        </div>
    );
}

// ─── Invoice number generator ─────────────────────────────────────────────────

/**
 * Generates the next HUB-{YYYYMMDD}-{seq}-INV invoice number.
 * Queries existing invoices for today's date to find the highest sequence.
 * Retries up to 3 times if a duplicate is found at insert time (race window).
 * For a fully atomic guarantee, add a UNIQUE(org_id, invoice_number) constraint
 * on the invoices table and handle the conflict at the DB level.
 * @param {object} supabase  Supabase client
 * @param {string} orgId     Current org_id
 * @param {number} [offset]  Extra offset added to maxSeq (used for retries)
 * @returns {Promise<string>}
 */
async function generateInvoiceNumber(supabase, orgId, offset = 0) {
    const today = new Date();
    const datePart = today.toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
    const prefix = `HUB-${datePart}-`;

    const { data } = await supabase
        .from('invoices')
        .select('invoice_number')
        .eq('org_id', orgId)
        // Breaker Panel: TEST invoices carry their own stored TEST- series (set by the database); the live series never counts them.
        .eq('is_test', false)
        .like('invoice_number', `${prefix}%`)
        .order('invoice_number', { ascending: false });

    let maxSeq = 0;
    if (data && data.length > 0) {
        for (const row of data) {
            const parts = row.invoice_number?.split('-');
            if (parts && parts.length >= 4) {
                const seq = parseInt(parts[2], 10);
                if (!isNaN(seq) && seq > maxSeq) maxSeq = seq;
            }
        }
    }
    const nextSeq = String(maxSeq + 1 + offset).padStart(3, '0');
    return `${prefix}${nextSeq}-INV`;
}

// ─── Manual Invoice Form Modal ────────────────────────────────────────────────

/**
 * Modal for creating a new invoice or editing an existing one.
 *
 * @param {object}   props
 * @param {object|null} props.invoice  Pre-filled invoice for edit mode; null for create.
 * @param {Array}    props.accounts    Available accounts for the Account selector.
 * @param {string}   props.orgId       Current org_id (required for insert).
 * @param {string}   [props.theme]     'dark' | 'light'
 * @param {function} props.onClose     Called when the modal is dismissed.
 * @param {function} props.onSaved     Called after a successful save with the saved invoice row.
 */
function InvoiceFormModal({ invoice, accounts, orgId, theme, onClose, onSaved }) {
    const isEdit = Boolean(invoice?.id);
    const isDark = theme === 'dark';

    const [form, setForm] = useState({
        account_id:          invoice?.account_id          ?? '',
        confirmation_number: invoice?.confirmation_number ?? '',
        subtotal:            invoice?.subtotal != null    ? String(invoice.subtotal) : '',
        tax:                 invoice?.tax != null         ? String(invoice.tax)     : '0',
        convenience_fee:     invoice?.convenience_fee != null ? String(invoice.convenience_fee) : '0',
        due_date:            invoice?.due_date            ?? '',
        notes:               invoice?.notes               ?? '',
        status:              invoice?.status              ?? 'pending',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError]   = useState(null);

    const subtotalNum     = parseFloat(form.subtotal)       || 0;
    const taxNum          = parseFloat(form.tax)            || 0;
    const convFeeNum      = parseFloat(form.convenience_fee)|| 0;
    const computedTotal   = subtotalNum + taxNum + convFeeNum;

    const inputClass = `w-full border rounded px-3 py-2 text-sm ${
        isDark
            ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
            : 'bg-white border-gray-300 text-gray-900'
    }`;
    const labelClass = `block text-xs font-semibold mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!form.account_id) {
            setError('Account / Client is required.');
            return;
        }
        if (!form.subtotal || isNaN(parseFloat(form.subtotal))) {
            setError('Subtotal must be a valid number.');
            return;
        }

        setSaving(true);
        try {
            let savedRow;
            if (isEdit) {
                const { data, error: updateError } = await supabaseClient
                    .from('invoices')
                    .update({
                        account_id:          form.account_id          || null,
                        confirmation_number: form.confirmation_number || null,
                        subtotal:            subtotalNum,
                        tax:                 taxNum,
                        convenience_fee:     convFeeNum,
                        total:               computedTotal,
                        due_date:            form.due_date             || null,
                        notes:               form.notes                || null,
                        status:              form.status               || 'pending',
                    })
                    .eq('id', invoice.id)
                    .select()
                    .single();
                if (updateError) throw updateError;
                savedRow = data;
            } else {
                // Retry up to 3 times to handle simultaneous-submission race on invoice_number.
                let insertError;
                for (let attempt = 0; attempt < 3; attempt++) {
                    const invoiceNumber = await generateInvoiceNumber(supabaseClient, orgId, attempt);
                    const { data: insertData, error: err } = await supabaseClient
                        .from('invoices')
                        .insert([{
                            org_id:              orgId,
                            invoice_number:      invoiceNumber,
                            account_id:          form.account_id          || null,
                            confirmation_number: form.confirmation_number || null,
                            subtotal:            subtotalNum,
                            tax:                 taxNum,
                            convenience_fee:     convFeeNum,
                            total:               computedTotal,
                            due_date:            form.due_date             || null,
                            notes:               form.notes                || null,
                            status:              form.status               || 'pending',
                        }])
                        .select()
                        .single();
                    if (!err) {
                        savedRow = insertData;
                        insertError = null;
                        break;
                    }
                    // Only retry on duplicate key violations; surface other errors immediately.
                    const isDuplicate = err.code === '23505' ||
                        (typeof err.message === 'string' && err.message.includes('duplicate'));
                    if (!isDuplicate) { insertError = err; break; }
                    insertError = err;
                }
                if (insertError) throw insertError;
            }
            onSaved(savedRow);
            onClose();
        } catch (err) {
            setError(err.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const accountLabel = (acct) =>
        acct.account_name || acct.company_name || acct.contact_name || acct.account || acct.id;

    return (
        <ModalSurface label={isEdit ? 'Edit Invoice' : 'New Invoice'} onClose={onClose}
            overlayClassName="ia-financials fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            panelClassName={`w-full max-w-lg rounded-lg shadow-xl p-6 space-y-4 ${isDark ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'}`}>
            <div className="ia-modal-body space-y-4">
                <h3 className="text-xl font-bold">{isEdit ? 'Edit Invoice' : 'New Invoice'}</h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Account / Client — required */}
                    <div>
                        <label className={labelClass}>Account / Client *</label>
                        <select
                            value={form.account_id}
                            onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                            className={inputClass}
                            required
                        >
                            <option value="">— Select account —</option>
                            {(accounts || []).map((acct) => (
                                <option key={acct.id} value={acct.id}>{accountLabel(acct)}</option>
                            ))}
                        </select>
                    </div>

                    {/* Booking / confirmation number — optional */}
                    <div>
                        <label className={labelClass}>Booking / Confirmation Number (optional)</label>
                        <input
                            type="text"
                            value={form.confirmation_number}
                            onChange={(e) => setForm({ ...form, confirmation_number: e.target.value })}
                            placeholder="e.g. HUB-20260823-010"
                            className={inputClass}
                        />
                    </div>

                    {/* Subtotal */}
                    <div>
                        <label className={labelClass}>Subtotal ($) *</label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.subtotal}
                            onChange={(e) => setForm({ ...form, subtotal: e.target.value })}
                            placeholder="0.00"
                            className={inputClass}
                            required
                        />
                    </div>

                    {/* Tax */}
                    <div>
                        <label className={labelClass}>Tax ($)</label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.tax}
                            onChange={(e) => setForm({ ...form, tax: e.target.value })}
                            placeholder="0.00"
                            className={inputClass}
                        />
                    </div>

                    {/* Convenience fee */}
                    <div>
                        <label className={labelClass}>Convenience Fee ($)</label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.convenience_fee}
                            onChange={(e) => setForm({ ...form, convenience_fee: e.target.value })}
                            placeholder="0.00"
                            className={inputClass}
                        />
                    </div>

                    {/* Computed total — read-only */}
                    <div className={`ia-financial-total rounded px-3 py-2 text-sm font-semibold ${isDark ? 'bg-gray-700 text-gray-100' : 'bg-gray-50 text-gray-800'}`}>
                        Total: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(computedTotal)}
                    </div>

                    {/* Due date */}
                    <div>
                        <label className={labelClass}>Due Date</label>
                        <input
                            type="date"
                            value={form.due_date}
                            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                            className={inputClass}
                        />
                    </div>

                    {/* Notes */}
                    <div>
                        <label className={labelClass}>Notes</label>
                        <textarea
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            rows={3}
                            className={inputClass}
                        />
                    </div>

                    {error && <p className="text-sm text-red-500">{error}</p>}

                    <div className="ia-modal-actions flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className={`border px-4 py-2 rounded text-sm ${isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-[var(--btn-fill)] hover:bg-[var(--btn-fill-hover)] text-[var(--btn-ink)] px-4 py-2 rounded text-sm disabled:opacity-60"
                        >
                            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Invoice'}
                        </button>
                    </div>
                </form>
            </div>
        </ModalSurface>
    );
}

function InvoicingView({ invoices: invoicesProp, accounts, orgId, theme, recurringBilling: recurringBillingProp, subAccounts: subAccountsProp, onDisputeResolution, isUser = false }) {
    const { allowed: financialEditAllowed } = useModuleAccess(isUser ? 'financial' : null, 'edit');
    const canEdit = !isUser || financialEditAllowed;
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    // ─── Invoice create/edit modal state ──────────────────────────────────────
    const [invoiceModal, setInvoiceModal] = useState(null); // null | { mode: 'create' } | { mode: 'edit', invoice }
    // Local list for optimistic updates after create/edit
    const [localInvoices, setLocalInvoices] = useState(null);
    const [viewMode, setViewMode] = useState('all'); // 'all' | 'rollup'
    const [expandedInvoiceIds, setExpandedInvoiceIds] = useState({});
    // Gate 11 — track which invoices have their override audit panel open.
    const [expandedOverrideAuditIds, setExpandedOverrideAuditIds] = useState({});
    const [expandedDisputeIds, setExpandedDisputeIds] = useState({});
    const [localInvoiceOverrides, setLocalInvoiceOverrides] = useState({});
    const [pendingDisputeAuditEntries, setPendingDisputeAuditEntries] = useState({});
    // Self-fetch active recurring_billing rows when the prop is not supplied by the parent.
    const [fetchedRecurringBilling, setFetchedRecurringBilling] = useState([]);
    const [fetchedSubAccounts, setFetchedSubAccounts] = useState([]);
    useEffect(() => {
        if (recurringBillingProp !== undefined) return; // prop supplied — skip self-fetch
        let cancelled = false;
        supabaseClient
            .from('recurring_billing')
            .select('account_id, status')
            .eq('status', 'active')
            .then(({ data, error }) => {
                if (!cancelled && !error && data) setFetchedRecurringBilling(data);
            });
        return () => { cancelled = true; };
    }, [recurringBillingProp]);
    const recurringBilling = recurringBillingProp !== undefined ? recurringBillingProp : fetchedRecurringBilling;

    // Self-fetch sub_accounts for rollup view when prop not supplied by parent.
    useEffect(() => {
        if (subAccountsProp !== undefined) return;
        let cancelled = false;
        supabaseClient
            .from('sub_accounts')
            .select('id, entity_id, parent_account_id, sub_account_name, rollup_to_parent, term_overrides')
            .eq('rollup_to_parent', true)
            .then(({ data, error }) => {
                if (!cancelled && !error && data) setFetchedSubAccounts(data);
            });
        return () => { cancelled = true; };
    }, [subAccountsProp]);
    const subAccounts = subAccountsProp !== undefined ? subAccountsProp : fetchedSubAccounts;

    // Open create modal when ?action=create is in the URL (e.g. from QuickActionsWidget).
    useEffect(() => {
        if (canEdit && searchParams.get('action') === 'create') {
            setInvoiceModal({ mode: 'create' });
            // Remove the query param so the modal doesn't re-open on back/forward navigation.
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('action');
                return next;
            }, { replace: true });
        }
    }, [canEdit, searchParams, setSearchParams]);

    const handleInvoiceSaved = useCallback((savedRow) => {
        setLocalInvoices((prev) => {
            const base = prev || [];
            const existingIdx = base.findIndex((inv) => inv.id === savedRow.id);
            if (existingIdx >= 0) {
                const updated = [...base];
                updated[existingIdx] = savedRow;
                return updated;
            }
            return [savedRow, ...base];
        });
        // Also apply to localInvoiceOverrides so edits show immediately.
        if (savedRow.id) {
            setLocalInvoiceOverrides((prev) => ({ ...prev, [savedRow.id]: savedRow }));
        }
    }, []);

    // Merge invoicesProp with any locally-created invoices (optimistic append).
    const invoices = useMemo(() => {
        const base = invoicesProp || [];
        if (!localInvoices) return base;
        // Prepend newly created invoices that aren't yet in the parent-supplied list.
        const existingIds = new Set(base.map((inv) => inv.id));
        const newOnes = localInvoices.filter((inv) => inv.id && !existingIds.has(inv.id));
        return [...newOnes, ...base];
    }, [invoicesProp, localInvoices]);

    // Breaker Panel G4: TEST invoices show (and count in the totals) while Invoices is in Test; once Live they drop out of the
    // list and the totals unless "Include TEST" is on.
    const invoicesMode = useModuleMode('invoices');
    const [includeTestToggle, setIncludeTestToggle] = useState(null);
    const includeTest = showTestRecords(invoicesMode, includeTestToggle);
    const effectiveInvoices = useMemo(
        () => (invoices || []).map((invoice) => localInvoiceOverrides[invoice.id] || invoice).filter((invoice) => includeTest || !invoice?.is_test),
        [invoices, localInvoiceOverrides, includeTest]
    );

    // Sub-account rollup — computed whenever invoices or subAccounts change.
    const rollupResult = useMemo(
        () => groupInvoicesForParentRollup(effectiveInvoices, subAccounts),
        [effectiveInvoices, subAccounts]
    );
     // Calculate invoice statistics
    const stats = useMemo(() => {
        console.log('[InvoicingView] Calculating stats from invoices:', effectiveInvoices.length || 0);
        if (effectiveInvoices.length === 0) {
            return { total: 0, paid: 0, outstanding: 0, totalAmount: 0, paidAmount: 0, outstandingAmount: 0 };
        }
         const paid = effectiveInvoices.filter(inv =>
            inv.lifecycle_status === 'paid' ||
            inv.lifecycle_status === 'closed' ||
            inv.status === 'paid' ||
            inv.status === 'Paid'
        );
        const outstanding = effectiveInvoices.filter(inv => {
            const ls = inv.lifecycle_status;
            if (ls) return ls === 'issued' || ls === 'partially_paid';
            return (
                inv.status === 'outstanding' ||
                inv.status === 'pending' ||
                inv.status === 'Pending'
            );
        });
         const paidAmount = paid.reduce((sum, inv) => sum + getInvoiceDisplayAmount(inv), 0);
        const outstandingAmount = outstanding.reduce((sum, inv) => sum + getInvoiceDisplayAmount(inv), 0);
       const totalAmount = effectiveInvoices.reduce((sum, inv) => sum + getInvoiceDisplayAmount(inv), 0);
         return {
           total: effectiveInvoices.length,
            paid: paid.length,
            outstanding: outstanding.length,
            totalAmount,
            paidAmount,
            outstandingAmount
        };
    }, [effectiveInvoices]);
     // Filter invoices based on search and status
    const filteredInvoices = useMemo(() => {
       if (effectiveInvoices.length === 0) return [];
        return effectiveInvoices.filter(invoice => {
            const matchesSearch = !searchTerm ||
                invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                invoice.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
           const disputeState = getDisputeReviewState(invoice);

           let matchesStatus;
           if (statusFilter === 'all') {
               matchesStatus = true;
           } else if (statusFilter === DISPUTE_STATES.DISPUTED || statusFilter === DISPUTE_STATES.UNDER_REVIEW) {
               matchesStatus = disputeState === statusFilter;
           } else if (statusFilter === 'paid') {
               matchesStatus =
                   invoice.lifecycle_status === 'paid' ||
                    invoice.lifecycle_status === 'closed' ||
                    invoice.status?.toLowerCase() === 'paid';
            } else if (statusFilter === 'pending') {
                matchesStatus =
                    invoice.lifecycle_status === 'issued' ||
                    invoice.lifecycle_status === 'partially_paid' ||
                    invoice.status?.toLowerCase() === 'outstanding' ||
                    invoice.status?.toLowerCase() === 'pending';
            } else if (statusFilter === 'overdue') {
                const state = invoice.lifecycle_status?.toLowerCase();
                const due = invoice.due_date ? new Date(invoice.due_date) : null;
                matchesStatus =
                    due && !isNaN(due) && due < new Date() &&
                    !TERMINAL_OR_SETTLED_STATES.has(state);
            } else {
                matchesStatus =
                    invoice.lifecycle_status?.toLowerCase() === statusFilter.toLowerCase() ||
                    invoice.status?.toLowerCase() === statusFilter.toLowerCase();
            }
             return matchesSearch && matchesStatus;
        });
    }, [effectiveInvoices, searchTerm, statusFilter]);

    // Legacy status badge (for rows without lifecycle_status).
    const getLegacyStatusBadge = (status) => {
        const statusLower = status?.toLowerCase() || '';
        if (statusLower === 'paid') {
            return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Paid</span>;
        } else if (statusLower === 'pending' || statusLower === 'outstanding') {
            return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Outstanding</span>;
        } else if (statusLower === 'overdue') {
            return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Overdue</span>;
        } else {
            return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{status}</span>;
        }
    };
     // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
    };
     // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const toggleInvoiceAssembly = useCallback((invoiceId) => {
        if (!invoiceId) return;
        setExpandedInvoiceIds((prev) => ({ ...prev, [invoiceId]: !prev[invoiceId] }));
    }, []);

    const toggleOverrideAudit = useCallback((invoiceId) => {
        if (!invoiceId) return;
        setExpandedOverrideAuditIds((prev) => ({ ...prev, [invoiceId]: !prev[invoiceId] }));
    }, []);

    const toggleDisputePanel = useCallback((invoiceId) => {
       if (!invoiceId) return;
       setExpandedDisputeIds((prev) => ({ ...prev, [invoiceId]: !prev[invoiceId] }));
    }, []);

    const applyLocalDisputeUpdate = useCallback((invoiceId, updatedInvoice, auditEntry) => {
       if (!invoiceId) return;

       setLocalInvoiceOverrides((prev) => ({ ...prev, [invoiceId]: updatedInvoice }));
       if (auditEntry) {
           setPendingDisputeAuditEntries((prev) => ({
               ...prev,
               [invoiceId]: [auditEntry, ...(prev[invoiceId] || [])],
           }));
       }
    }, []);
     return (
       <div className={`ia-financials rounded-lg shadow-md p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Invoice create/edit modal */}
            {canEdit && invoiceModal && (
                <InvoiceFormModal
                    invoice={invoiceModal.mode === 'edit' ? invoiceModal.invoice : null}
                    accounts={accounts}
                    orgId={orgId}
                    theme={theme}
                    onClose={() => setInvoiceModal(null)}
                    onSaved={handleInvoiceSaved}
                />
            )}
            {/* Header */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h3 className={`text-2xl font-bold mb-2 ${'text-[var(--ink)]'}`}>Invoicing</h3>
                    <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Manage and track your invoices</p>
                </div>
                <div className="flex items-center gap-3">
                    {/* + New Invoice button */}
                    {canEdit && <button
                        type="button"
                        onClick={() => setInvoiceModal({ mode: 'create' })}
                        className="rounded bg-[var(--btn-fill)] px-4 py-2 text-sm font-medium text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)]"
                    >
                        + New Invoice
                    </button>}
                    {/* View mode toggle — show rollup tab only when sub-accounts exist */}
                    {subAccounts.length > 0 && (
                        <div className={`flex rounded-lg border overflow-hidden text-sm font-medium ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>
                            <button
                                onClick={() => setViewMode('all')}
                                className={`px-4 py-2 ${viewMode === 'all' ? 'bg-[var(--btn-fill)] text-[var(--btn-ink)]' : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                                All Invoices
                            </button>
                            <button
                                onClick={() => setViewMode('rollup')}
                                className={`px-4 py-2 ${viewMode === 'rollup' ? 'bg-[var(--btn-fill)] text-[var(--btn-ink)]' : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                                Parent Rollup
                            </button>
                        </div>
                    )}
                </div>
            </div>
             {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-blue-50'}`}>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-blue-600'} font-semibold`}>Total Invoices</p>
                    <p className="text-2xl font-bold text-[var(--ink)]">{stats.total}</p>
                    <p className={`ia-financial-total text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-blue-700'}`}>{formatCurrency(stats.totalAmount)}</p>
                </div>
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-green-50'}`}>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-green-600'} font-semibold`}>Paid</p>
                    <p className="text-2xl font-bold text-[var(--ink)]">{stats.paid}</p>
                    <p className={`ia-financial-total text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-green-700'}`}>{formatCurrency(stats.paidAmount)}</p>
                </div>
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-yellow-50'}`}>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-yellow-600'} font-semibold`}>Outstanding</p>
                    <p className="text-2xl font-bold text-[var(--ink)]">{stats.outstanding}</p>
                    <p className={`ia-financial-total text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-yellow-700'}`}>{formatCurrency(stats.outstandingAmount)}</p>
                </div>
            </div>
             {/* Search and Filter — shown in 'all' mode only */}
            {viewMode === 'all' && (
            <ResponsiveRow className="ia-financial-filters flex flex-col md:flex-row gap-4 mb-6">
                <input
                    type="text"
                    placeholder="Search invoices..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`flex-1 px-4 py-2 rounded-lg border ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
                <button type="button" className="ia-hollow-pill" aria-pressed={includeTest} onClick={() => setIncludeTestToggle(!includeTest)} data-testid="include-test-toggle">
                    Include TEST
                </button>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className={`px-4 py-2 rounded-lg border ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                    <option value="all">All Status</option>
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="declined">Declined</option>
                    <option value="issued">Issued</option>
                    <option value="partially_paid">Partially Paid</option>
                    <option value="paid">Paid</option>
                    <option value="closed">Closed</option>
                    <option value="void">Void</option>
                    <option value="canceled">Canceled</option>
                    <option value="refunded">Refunded</option>
                    <option value="written_off">Written Off</option>
                    <option value="disputed">Disputed</option>
                    <option value="under_review">Under Review</option>
                    <option value="overdue">Overdue</option>
                    <option value="pending">Outstanding (legacy)</option>
                </select>
            </ResponsiveRow>
            )}

            {/* ── Parent Rollup View ─────────────────────────────────────────── */}
            {viewMode === 'rollup' && (
                <div className="space-y-4 mb-6">
                    {Object.keys(rollupResult.rollupGroups).length === 0 ? (
                        <div className="text-center py-12">
                            <div className="text-6xl mb-4">🏢</div>
                            <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                No sub-account rollup data
                            </p>
                            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                                Invoices will appear here once they are linked to sub-account entities.
                            </p>
                        </div>
                    ) : (
                        Object.values(rollupResult.rollupGroups).map((group) => (
                            <div
                                key={group.parent_account_id}
                                className={`rounded-lg border ${theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}
                            >
                                {/* Parent account header */}
                                <div className={`flex items-center justify-between px-4 py-3 rounded-t-lg ${theme === 'dark' ? 'bg-gray-600' : 'bg-white border-b border-gray-200'}`}>
                                    <div>
                                        <span className={`font-semibold text-sm ${'text-[var(--ink)]'}`}>
                                            {accounts?.find(a => a.id === group.parent_account_id)?.account_name
                                              || accounts?.find(a => a.id === group.parent_account_id)?.account
                                              || accounts?.find(a => a.id === group.parent_account_id)?.company_name
                                              || 'Parent Account'}
                                        </span>
                                        <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {group.parent_account_id}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <div className={`text-sm font-bold ${'text-[var(--ink)]'}`}>
                                            {formatCurrency(group.rollupTotal)}
                                        </div>
                                        {group.totalDiscount > 0 && (
                                            <div className={`text-xs ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                                                −{formatCurrency(group.totalDiscount)} in overrides
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* Sub-account line items */}
                                <div className={`divide-y ${theme === 'dark' ? 'divide-gray-600' : 'divide-gray-200'}`}>
                                    {group.entries.map((entry) => (
                                        <div
                                            key={`${entry.sub_account_id}-${entry.invoice.id}`}
                                            className={`flex items-center justify-between px-4 py-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                                        >
                                            <div>
                                                <span className="font-medium">{entry.sub_account_name}</span>
                                                <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    {entry.invoice.invoice_number || entry.invoice.id}
                                                </span>
                                                {entry.discountApplied > 0 && (
                                                    <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                                                        ({entry.termOverrides.discount_pct}% override)
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <div className="font-semibold">{formatCurrency(entry.adjustedAmount)}</div>
                                                {entry.discountApplied > 0 && (
                                                    <div className={`text-xs line-through ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                                        {formatCurrency(entry.rawAmount)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

             {/* Invoice Table — shown in 'all' mode only */}
            {viewMode === 'all' && (filteredInvoices.length === 0 ? (
                <div className="text-center py-12">
                    <div className="text-6xl mb-4">📄</div>
                    <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {invoices && invoices.length > 0 ? 'No invoices match your filters' : 'No invoices yet'}
                    </p>
                    <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                        {invoices && invoices.length > 0 ? 'Try adjusting your search or filters' : 'Create your first invoice to get started'}
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <ResponsiveTable className="w-full">
                        <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                            <tr>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Invoice #</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Client</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Date</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Due Date</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Amount</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Status</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Badges</th>
                                {canEdit && <th className={`px-4 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Actions</th>}
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                            {filteredInvoices.map((invoice) => {
                                const derivedBadges = computeDerivedBadges(invoice, recurringBilling);
                                const assemblySummary = buildInvoiceAssemblySummary(invoice);
                                const isExpanded = Boolean(expandedInvoiceIds[invoice.id]);
                                const hasFinalAmount = !(invoice?.final_amount === '' || invoice?.final_amount === null || invoice?.final_amount === undefined);
                                const displayAmount = !hasFinalAmount && assemblySummary.isDeterministic
                                    ? assemblySummary.total
                                    : getInvoiceDisplayAmount(invoice);

                                // Gate 11 — detect any overridden line item on this invoice.
                                const lineItems = Array.isArray(invoice.line_items) ? invoice.line_items : [];
                                const hasOverride = invoice.is_overridden === true || lineItems.some(isLineItemOverridden);
                                const isOverrideAuditOpen = Boolean(expandedOverrideAuditIds[invoice.id]);
                                const disputeState = getDisputeReviewState(invoice);
                                const hasCreditNotes = Array.isArray(invoice.credit_notes) && invoice.credit_notes.length > 0;
                                const hasDisputeWorkflow = Boolean(disputeState || invoice.resolution_type || hasCreditNotes);
                                const isDisputePanelOpen = Boolean(expandedDisputeIds[invoice.id]);
                                const disputeBadge = getDisputeBadge(disputeState, invoice.resolution_type, hasCreditNotes);
                                const disputePanelId = `invoice-dispute-panel-${invoice.id}`;

                                return (
                                    <React.Fragment key={invoice.id}>
                                    <tr className={`hover:${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'} transition-colors`}>
                                        <ResponsiveCell label="Invoice #" className={`px-4 py-3 text-sm font-medium ${'text-[var(--ink)]'}`}>
                                            {displayNumber(invoice.invoice_number, invoice.is_test) || 'N/A'}
                                            {invoice.is_test && <TestChip className="ml-2" />}
                                        </ResponsiveCell>
                                        <ResponsiveCell label="Client" className={`px-4 py-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            {invoice.client_name || 'Unknown Client'}
                                        </ResponsiveCell>
                                        <ResponsiveCell label="Date" className={`px-4 py-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            {formatDate(invoice.invoice_date || invoice.created_at)}
                                        </ResponsiveCell>
                                        <ResponsiveCell label="Due Date" className={`px-4 py-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            {formatDate(invoice.due_date)}
                                        </ResponsiveCell>
                                        <ResponsiveCell label="Amount" className={`ia-financial-amount px-4 py-3 text-sm font-semibold ${'text-[var(--ink)]'}`}>
                                            {formatCurrency(displayAmount)}
                                            {assemblySummary.isDeterministic && (
                                                <button
                                                    type="button"
                                                    onClick={() => toggleInvoiceAssembly(invoice.id)}
                                                    className={`mt-1 block text-xs font-medium underline ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}
                                                >
                                                    {isExpanded ? 'Hide breakdown' : 'Show breakdown'}
                                                </button>
                                            )}
                                            {assemblySummary.isDeterministic && isExpanded && (
                                                <div className={`mt-2 space-y-1 text-xs font-normal ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                                    <div>Fares: {formatCurrency(assemblySummary.fares)}</div>
                                                    {assemblySummary.fees > 0 && <div>Fees: {formatCurrency(assemblySummary.fees)}</div>}
                                                    {assemblySummary.surcharges > 0 && <div>Surcharges: {formatCurrency(assemblySummary.surcharges)}</div>}
                                                    {assemblySummary.discounts > 0 && <div>Discounts: −{formatCurrency(assemblySummary.discounts)}</div>}
                                                    {assemblySummary.tax > 0 && <div>Tax: {formatCurrency(assemblySummary.tax)}</div>}
                                                    {assemblySummary.cardFee > 0 && <div>Card Fee: {formatCurrency(assemblySummary.cardFee)}</div>}
                                                </div>
                                            )}
                                            {getInvoiceDiscountLines(invoice).map((line) => (
                                            <div key={`${invoice.id}-${line.code}-${line.label}`} className={`text-xs font-normal mt-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {line.label}: {formatCurrency(line.amount)}
                                            </div>
                                            ))}
                                        </ResponsiveCell>
                                        <ResponsiveCell label="Status" className="px-4 py-3 text-sm">
                                            {invoice.lifecycle_status
                                                ? getLifecycleBadge(invoice.lifecycle_status)
                                                : getLegacyStatusBadge(invoice.status)}
                                        </ResponsiveCell>
                                        <ResponsiveCell label="Badges" className="px-4 py-3 text-sm">
                                            <div className="flex flex-wrap gap-1">
                                                {derivedBadges}
                                                {/* Gate 11 — amber override badge; click opens audit drill-down */}
                                                {hasOverride && (
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleOverrideAudit(invoice.id)}
                                                        aria-expanded={isOverrideAuditOpen}
                                                        className="focus:outline-none"
                                                    >
                                                        {getOverrideBadge()}
                                                    </button>
                                                )}
                                                {hasDisputeWorkflow && disputeBadge && (
                                                    canEdit
                                                        ? <button
                                                            type="button"
                                                            onClick={() => toggleDisputePanel(invoice.id)}
                                                            aria-expanded={isDisputePanelOpen}
                                                            aria-controls={disputePanelId}
                                                            aria-label={`Toggle dispute details for invoice ${invoice.invoice_number || invoice.id}`}
                                                            className="focus:outline-none"
                                                        >
                                                            {disputeBadge}
                                                        </button>
                                                        : disputeBadge
                                                )}
                                            </div>
                                        </ResponsiveCell>
                                        {canEdit && <ResponsiveCell label="Actions" className="px-4 py-3 text-sm">
                                            <div className="flex flex-wrap items-center gap-2">
                                            <ChargeInvoiceAction
                                                invoice={invoice}
                                                onInvoiceStatusChange={(updated) => setLocalInvoiceOverrides((previous) => ({
                                                    ...previous,
                                                    [invoice.id]: { ...invoice, ...previous[invoice.id], ...updated },
                                                }))}
                                            />
                                            <button
                                                type="button"
                                                aria-label={`Edit invoice ${invoice.invoice_number || invoice.id}`}
                                                onClick={() => setInvoiceModal({ mode: 'edit', invoice })}
                                                className={`px-2 py-1 rounded text-xs font-medium border ${
                                                    theme === 'dark'
                                                        ? 'border-gray-500 text-gray-300 hover:bg-gray-700'
                                                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                                                }`}
                                            >
                                                ✏️ Edit
                                            </button>
                                            </div>
                                        </ResponsiveCell>}
                                    </tr>
                                    {/* Gate 11 — override audit drill-down row */}
                                    {hasOverride && isOverrideAuditOpen && (
                                        <tr className={theme === 'dark' ? 'bg-gray-800' : 'bg-amber-50'}>
                                            <td colSpan={canEdit ? 8 : 7} className="px-6 py-3">
                                                <p className={`text-xs font-semibold mb-2 ${theme === 'dark' ? 'text-amber-300' : 'text-amber-800'}`}>
                                                    Override audit trail
                                                </p>
                                                <OverrideAuditPanel invoiceId={invoice.id} theme={theme} />
                                            </td>
                                        </tr>
                                    )}
                                    {canEdit && hasDisputeWorkflow && isDisputePanelOpen && (
                                        <tr className={theme === 'dark' ? 'bg-gray-800' : 'bg-rose-50'}>
                                            <td id={disputePanelId} colSpan={canEdit ? 8 : 7} className="px-6 py-3">
                                                <DisputeResolutionPanel
                                                    invoice={invoice}
                                                    theme={theme}
                                                    pendingAuditEntries={pendingDisputeAuditEntries[invoice.id] || []}
                                                    onStartReview={({ updatedInvoice, auditEntry }) => {
                                                        applyLocalDisputeUpdate(invoice.id, updatedInvoice, auditEntry);
                                                    }}
                                                    onResolve={({ updatedInvoice, auditEntry, creditNote }) => {
                                                        applyLocalDisputeUpdate(invoice.id, updatedInvoice, auditEntry);
                                                        if (typeof onDisputeResolution === 'function') {
                                                            onDisputeResolution({
                                                                invoiceId: invoice.id,
                                                                updatedInvoice,
                                                                auditEntry,
                                                                creditNote,
                                                            });
                                                        }
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </ResponsiveTable>
                </div>
            ))}
        </div>
    );
}


export default InvoicingView;
